// Shared open/closed state for the Help assistant, so the toolbar toggle button
// (in the Dashboard) and the slide-out panel (mounted globally) stay in sync.
// Open/closed persists in the user's preference record.
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';
import { api } from '../services/api';

interface HelpContextValue {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  close: () => void;
}

const HelpContext = createContext<HelpContextValue | null>(null);

export function HelpProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getPreferences()
      .then((p) => { if (!cancelled && p.help_panel_open) setIsOpen(true); })
      .catch(() => { /* default closed */ });
    return () => { cancelled = true; };
  }, []);

  const persist = useCallback((v: boolean) => {
    api.updatePreferences({ help_panel_open: v }).catch(() => { /* best effort */ });
  }, []);

  const toggle = useCallback(() => setIsOpen((prev) => { persist(!prev); return !prev; }), [persist]);
  const open = useCallback(() => setIsOpen((prev) => { if (!prev) persist(true); return true; }), [persist]);
  const close = useCallback(() => setIsOpen((prev) => { if (prev) persist(false); return false; }), [persist]);

  return <HelpContext.Provider value={{ isOpen, toggle, open, close }}>{children}</HelpContext.Provider>;
}

export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be used within HelpProvider');
  return ctx;
}

// Toggle button sized to sit next to the grid/kanban view switch. Filled blue
// so it reads clearly as the way to open Help.
export function HelpToggleButton() {
  const { isOpen, toggle } = useHelp();
  return (
    <button
      onClick={toggle}
      data-help-target="mainapp.viewhelp"
      title="Help & tips"
      aria-label="Help & tips"
      aria-pressed={isOpen}
      className={`ml-1 p-1.5 rounded-md text-white shadow-sm ring-1 ring-sky-300 transition-colors ${
        isOpen ? 'bg-sky-700 hover:bg-sky-800' : 'bg-sky-500 hover:bg-sky-600'
      }`}
    >
      <HelpCircle className="w-4 h-4" />
    </button>
  );
}
